import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import '../../assets/css/blog.css';
import SkeletonBlog from '../../shared/components/skeletonblog';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

   // Inicializa parâmetros da URL
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page')) || 1;
    const categoryFromUrl = searchParams.get('category') || '';
    const searchFromUrl = searchParams.get('search') || '';
    
    setCurrentPage(pageFromUrl);
    setSelectedCategory(categoryFromUrl);
    setSearchTerm(searchFromUrl);
  }, []);

  
    const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Construir URL com todos os parâmetros
      let postsUrl = `https://wordpress.zauso-dev.com.br/wp-json/wp/v2/posts?_embed&per_page=3&page=${currentPage}`;
      
      if (selectedCategory) {
        postsUrl += `&categories=${selectedCategory}`;
      }
      if (searchTerm) {
        postsUrl += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      console.log('Fetching:', postsUrl);
      
      const postsResponse = await fetch(postsUrl);
      
      // Extrair informações de paginação dos headers
      const totalPostsHeader = postsResponse.headers.get('X-WP-Total');
      const totalPagesHeader = postsResponse.headers.get('X-WP-TotalPages');
      
      if (totalPostsHeader) setTotalPosts(parseInt(totalPostsHeader));
      if (totalPagesHeader) setTotalPages(parseInt(totalPagesHeader));
      
      const postsData = await postsResponse.json();
      setPosts(postsData);
      
      // Atualizar URL com parâmetros atuais
      const newSearchParams = new URLSearchParams();
      if (currentPage > 1) newSearchParams.set('page', currentPage.toString());
      if (selectedCategory) newSearchParams.set('category', selectedCategory);
      if (searchTerm) newSearchParams.set('search', searchTerm);
      setSearchParams(newSearchParams);
      
      // Fetch categorias apenas uma vez
      if (categories.length === 0) {
        const catsResponse = await fetch('https://wordpress.zauso-dev.com.br/wp-json/wp/v2/categories');
        const catsData = await catsResponse.json();
        setCategories(catsData);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory, searchTerm, categories.length, setSearchParams]);

 // Efeito principal
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToPage = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => goToPage(currentPage + 1);
  const goToPrevPage = () => goToPage(currentPage - 1);

  // Handlers de filtros
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset para primeira página ao mudar filtro
  };

 
  // Formatar número
  const formatNumber = (num) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  // Gerar array de páginas para exibição
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Mostrar todas as páginas
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Lógica para mostrar páginas com ellipsis
      let startPage, endPage;
      
      if (currentPage <= 3) {
        startPage = 1;
        endPage = maxPagesToShow;
      } else if (currentPage + 2 >= totalPages) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Adicionar ellipsis se necessário
      if (startPage > 1) {
        pageNumbers.unshift('...');
        pageNumbers.unshift(1);
      }
      if (endPage < totalPages) {
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };
  
  // Redireciona para detalhes do post
  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  // Formata data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Extrai categorias do post
  const getPostCategories = (post) => {
    return post._embedded?.['wp:term']?.[0] || [];
  };

  // Obtém imagem destacada
  const getFeaturedImage = (post) => {
    return post._embedded?.['wp:featuredmedia']?.[0];
  };

  return (
    <div className="blog-container">
      {/* Header do Blog */}
      <header className="blog-header">
        <h1 className="text-center font-bold mb-5 text-white text-font-family">Blog</h1>
        <div className="text-center text-color-site text-font-family">
          <p className="lead">
            Artigos, tutoriais e novidades sobre tecnologia e desenvolvimento
          </p>
        </div>
      </header>

      {/* Filtros e Busca */}
      <div className="blog-controls">
        <div className="category-filters">
          <div className="filter-label">Filtrar por:</div>
          <div className="category-buttons">
            <button
              className={`category-btn ${selectedCategory === '' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('')}
            >
              Todos
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-btn ${selectedCategory === cat.id.toString() ? 'active' : ''}`}
                onClick={() => handleCategoryChange(cat.id.toString())}
              >
                {cat.name} ({cat.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
       <SkeletonBlog cardsCount={3}  />
      )}

      {/* Posts Grid */}
      {!loading && posts.length > 0 && (
        
        <div className="posts-grid">
          {posts.map(post => {
            const featuredImage = getFeaturedImage(post);
            const postCategories = getPostCategories(post);
            
            return (
              <article 
                key={post.id} 
                className="post-card"
                onClick={() => handlePostClick(post.slug)}
              >
                {/* Imagem Destacada */}
                {featuredImage && (
                  <div className="post-image-container">
                    <img
                      src={featuredImage.source_url}
                      alt={featuredImage.alt_text || post.title.rendered}
                      className="post-image"
                      loading="lazy"
                    />
                    <div className="image-overlay"></div>
                  </div>
                )}

                {/* Categorias */}
                {postCategories.length > 0 && (
                  <div className="post-categories">
                    {postCategories.slice(0, 2).map(cat => (
                      <span key={cat.id} className="post-category">
                        {cat.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Conteúdo do Card */}
                <div className="post-content">
                  <h2 
                    className="post-title"
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                  />
                  
                  <div className="post-excerpt">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: post.excerpt.rendered.replace(/<[^>]+>/g, '').substring(0, 120) + '...' 
                      }}
                    />
                  </div>

                  {/* Meta informações */}
                  <div className="post-meta">
                    <div className="post-date">
                      📅 {formatDate(post.date)}
                    </div>
                    {post._embedded?.author?.[0] && (
                      <div className="post-author">
                        👤 {post._embedded.author[0].name}
                      </div>
                    )}
                  </div>

                  {/* Botão de ação */}
                  <div className="post-actions">
                    <button className="read-more-btn">
                      Ler Artigo →
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && posts.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>Nenhum artigo encontrado</h3>
          <p>
            {searchTerm || selectedCategory 
              ? "Tente ajustar os filtros ou busca"
              : "Em breve publicaremos novos artigos!"
            }
          </p>
          {(searchTerm || selectedCategory) && (
            <button 
              className="reset-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
              }}
            >
              Limpar Filtros
            </button>
          )}
        </div>
      )}

      {/* Paginação (opcional) */}
      {!loading && posts.length > 0 && (
        <div className="pagination">
          <button 
              className="pagination-btn prev" 
              onClick={goToPrevPage}
              disabled={currentPage === 1}
            >
              ← Anterior
          </button>

          <div className="page-numbers">
              {getPageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                ) : (
                  <button
                    key={pageNum}
                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              ))}
            </div>

           <div className="page-info text-color-site text-font-family">
              <span className="current-page">Página {currentPage} de {totalPages}</span>
           </div>

           <button 
              className="pagination-btn next" 
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Próxima →
           </button>
      
          
         
        </div>
      )}
    </div>
  );
};

export default Blog;